import * as StringUtils from "./StringUtils";
import type { TextElement } from "./LocaleUtils";

export class Translatable {
	public readonly vars: Record<string, TextElement | null> = {};
	public props: any = null;
	public readonly id: string;

	constructor(
		id: string,
		vars: Record<string, TextElement | null> = {},
		props = null,
	) {
		this.id = id;
		this.vars = vars;
		this.props = props;
	}

	clone(): Translatable {
		return new Translatable(this.id, this.vars);
	}

	toUpperCase(): MappedTranslatable {
		return this.map((string) => string.toUpperCase());
	}

	toTitleCase(): MappedTranslatable {
		return this.map(StringUtils.toTitleCase);
	}

	toLowerCase(): MappedTranslatable {
		return this.map((string) => string.toLowerCase());
	}

	substr(start: number, length?: number): MappedTranslatable {
		return this.map((string) => string.substr(start, length));
	}

	process(toText: (translatable: Translatable) => string): string {
		return toText(this);
	}

	toFallback(): string {
		return `%${this.id}`;
	}

	map(func: (text: string) => string): MappedTranslatable {
		return new MappedTranslatable(this.id, func, this.vars);
	}

	static compose(
		elements: TextElement[],
		processor: (...text: string[]) => string,
	): ComposedTranslatable {
		return new ComposedTranslatable(elements, processor);
	}

	with(vars: Record<string, TextElement | null>): Translatable {
		return new Translatable(this.id, { ...this.vars, ...(vars || {}) });
	}

	static of(text: TextElement): Translatable {
		if (text instanceof Translatable) {
			return text;
		}
		if (typeof text === "string" && text.startsWith("%") && text.length > 1) {
			return new Translatable(text.substr(1));
		}
		return new LiteralTranslatable(text);
	}
}

export class LiteralTranslatable extends Translatable {
	private readonly content: string | number;

	constructor(content: string | number) {
		super(StringUtils.uuid(6));
		this.content = content;
	}

	toFallback(): string {
		return this.content?.toString() || "";
	}

	process(toText: (translatable: Translatable) => string): string {
		return this.content?.toString();
	}
}

export class MappedTranslatable extends Translatable {
	private readonly processor: (text: string) => string;

	constructor(id: string, processor: (text: string) => string, vars = {}) {
		super(id, vars);
		this.processor = processor;
	}

	toFallback(): string {
		const text = super.toFallback();
		return `mapped(${text})`;
	}

	process(toText: (translatable: Translatable) => string): string {
		const text = toText(this);
		return this.processor.call(this, text);
	}
}

export class ComposedTranslatable extends Translatable {
	private readonly processor: (...text: string[]) => string;
	private readonly elements: TextElement[];

	constructor(
		elements: TextElement[],
		processor: (...text: string[]) => string,
		vars: Record<string, string> = {},
	) {
		super(ComposedTranslatable.getId(elements), vars);
		this.elements = elements.map((element) => Translatable.of(element));
		this.processor = processor;
	}

	toFallback(): string {
		return this.processor.apply(
			this,
			this.elements.map((text) =>
				text instanceof Translatable ? text.toFallback() : text,
			) as any[],
		);
	}

	static getId(components: TextElement[]): string {
		return components
			.map((c) => (c instanceof Translatable ? `%${c.id}` : c))
			.join("+");
	}

	process(toText: (translatable: Translatable) => string): string {
		return this.processor.apply(
			this,
			this.elements.map((text) =>
				text instanceof Translatable ? text.process(toText) : text,
			) as any[],
		);
	}
}

export const translatable = (id: string): Translatable => {
	return new Translatable(id);
};
